/**
 * Session creation, seating, objectives and Start shift.
 */
import { OBJECTIVES } from './content/objectives.ts';
import { LAYER_WILDCARDS, PACINGS, PRESETS } from './content/presets.ts';
import { buildTableDeck } from './deck.ts';
import { hashSeed, type Rng } from './prng.ts';
import { ensure, place, reject, seatedRoles, table, type Ctx, emit } from './rules/common.ts';
import { publishBedCounts } from './rules/tick.ts';
import type {
  Dashboard,
  LayerKey,
  Role,
  ScriptTurn,
  SeatState,
  SeatStats,
  SessionConfig,
  SessionState,
  TableState,
} from './types.ts';


export const ENGINE_VERSION = '3.1.0';

export function emptyDashboard(): Dashboard {
  return {
    saved: 0, harmed: 0, died: 0, diverted: 0, accepted: 0, early: 0, bounceBacks: 0, handedOver: 0, overflowDelays: 0,
    scans: 0, theatre: 0, icuBedTurns: 0, calls: 0, objectives: 0, objectivesDealt: 0, unstaffedPatientTurns: 0, bloodUsed: 0, ventShortTurns: 0,
    idleCapacity: 0,
  };
}

export function emptyStats(): SeatStats {
  return {
    actionsByTurn: {}, actionsLost: 0, discharges: 0, referralsOut: 0, unconfirmedTreats: 0, unconfirmedWrongHighCrit: 0,
    confirmedBeforeTreat: 0, vipCompleted: false, deteriorationsOnList: 0, deathsByTurn: {}, theatreSteps: 0, anaesthetics: 0,
    helpGiven: 0, helpReceived: 0, referralsIn: 0, referralsToCorridor: 0, earlyPassed: 0, earlyBounced: 0, patientsAtStartOfTurn: {},
  };
}

const BED_COUNTS: Record<Role, number> = { EM: 4, SURG: 4, PHYS: 5, PSYCH: 3, ICU: 2 };

export function newSeat(role: Role, name: string | null, bot: boolean, icuBeds = 2): SeatState {
  const n = role === 'ICU' ? icuBeds : BED_COUNTS[role];
  return {
    role, name, bot, objective: null,
    actions: { available: 0, spent: 0 }, beds: Array.from({ length: n }, () => null), corridor: [],
    lastActedOn: null, freeAssessUsedTurn: 0, e22UsedTurn: 0, staff: 0, stats: emptyStats(),
  };
}

function newTable(no: number): TableState {
  return {
    no, fiveSeat: true, seats: {}, drafts: {}, consents: [], receipts: [], tests: [], pendingTransfers: [], overflow: {}, lead: 'EM', compendiumHolder: 'EM', deck: [], bounceSlot: [],
    board: {
      scannerUsed: 0,
      scannerUsedBy: [null, null],
      theatre: { booking: null, anaesthetic: null, used: false, ventTaken: false },
      secondTheatre: null,
      allocations: { scanner: [null, null], theatre: null },
      divert: false,
      bedCounts: {
        EM: { used: 0, cap: 4, corridor: 0 }, SURG: { used: 0, cap: 4, corridor: 0 }, PHYS: { used: 0, cap: 5, corridor: 0 },
        PSYCH: { used: 0, cap: 3, corridor: 0 }, ICU: { used: 0, cap: 2, corridor: 0 },
      },
      staffLoans: [], staffAdjust: [], vents: null, blood: null,
    },
    openCards: [], handedOver: [], divertNotice: [], interrupts: [], prompts: [], pending: [], locums: [], bookings: [], callsForHelp: 0,
    timeOut: { used: false, turn: null }, dashboard: emptyDashboard(), trapList: [], disputes: [], published: { saved: 0, harmed: 0, died: 0 },
    effects: {
      physicianAwayUntil: 0, sickLeave: null, cover: null, decree: null, clusterLeft: 0,
      leadPenalty: null, freeAssessE16Turn: 0, e7FirstDischargeUsed: [], fullMoonTurn: 0,
    },
    tableStats: { firstOverflowTurn: null, scansTotal: 0, divertTurns: 0, callTurns: [], stepDownLate: 0, stepDownOnTime: 0, stepDownWaiting: {} },
  };
}

export interface CreateSessionInput {
  name: string;
  preset: SessionConfig['preset'];
  tables: number;
  pacing?: SessionConfig['pacing'];
  layers?: Partial<Record<LayerKey, boolean>>;
  ringOrder?: number[];
  seed?: string;
  clockScale?: number;
}

/** Create a new session in SETUP. Pure. */
export function createSession(input: CreateSessionInput): SessionState {
  const preset = PRESETS[input.preset];
  const nTables = Math.max(1, Math.min(12, Math.round(input.tables)));
  const seed = input.seed ?? 'nightshift';
  const pacing = input.pacing ?? PACINGS.STANDARD;
  const config: SessionConfig = {
    name: input.name,
    preset: input.preset,
    tables: nTables,
    pacing,
    layers: { ...preset.layers, ...(input.layers ?? {}) },
    ringOrder: input.ringOrder && input.ringOrder.length === nTables ? input.ringOrder : Array.from({ length: nTables }, (_, i) => i + 1),
    seed,
    clockScale: input.clockScale ?? 1,
  };
  return {
    v: 1,
    config,
    seq: 0,
    rng: hashSeed(seed),
    counters: { patient: 0, handover: 0, interrupt: 0, prompt: 0, misc: 0 },
    clock: { turn: 0, phase: 'SETUP', paused: false, hold: false, phaseEndsAt: null, remainingMs: null, lastTurn: pacing.scriptTurns.length, startedAt: null },
    script: [],
    room: { queuedInjects: [], activeEvents: [], thisTurnEvents: [], e35Held: false, e35Applied: false, broadcast: null, spareReds: [] },
    tables: Array.from({ length: nTables }, (_, i) => newTable(i + 1)),
    patients: {},
    backs: {},
  };
}

// ───────────────────────────── seating ─────────────────────────────

/** Clear consent and readiness when a seat controller is explicitly replaced. */
export function invalidateSeatCommitments(t: TableState, role: Role): void {
  const affected = new Set<Role>([role]);
  for (const consent of t.consents.filter(c => c.sender === role || c.receiver === role)) {
    affected.add(consent.sender);
    affected.add(consent.receiver);
  }
  t.consents = t.consents.filter(c => c.sender !== role && c.receiver !== role);
  for (const r of affected) {
    const plan = t.drafts[r];
    if (!plan) continue;
    plan.ready = false;
    plan.revision++;
  }
}

export function claimSeat(ctx: Ctx, tableNo: number, role: Role, name: string, bot: boolean): void {
  const s = ctx.s;
  const t = table(s, tableNo);
  const icuBeds = PRESETS[s.config.preset].icuBeds;
  const existing = t.seats[role];
  if (existing) {
    // re-claim (new device or bot ↔ human swap) keeps the seat's play state
    invalidateSeatCommitments(t, role);
    existing.name = name;
    existing.bot = bot;
  } else {
    ensure(s.clock.phase === 'SETUP', 'The shift has started; that seat was empty at the start.');
    t.seats[role] = newSeat(role, name, bot, icuBeds);
  }
  emit(ctx, { type: 'SEAT_CLAIMED', table: tableNo, seat: role, text: `${name} took the ${role} seat${bot ? ' (bot)' : ''}.` });
}

export function releaseSeat(ctx: Ctx, tableNo: number, role: Role): void {
  const t = table(ctx.s, tableNo);
  const st = t.seats[role];
  if (!st) return;
  invalidateSeatCommitments(t, role);
  if (ctx.s.clock.phase === 'SETUP') delete t.seats[role];
  else st.name = null;
  emit(ctx, { type: 'SEAT_RELEASED', table: tableNo, seat: role, text: `Seat ${role} released.` });
}

// ───────────────────────────── script ─────────────────────────────

export function buildScript(s: SessionState, rng: Rng): ScriptTurn[] {
  const preset = PRESETS[s.config.preset];
  const pile = [...preset.wildcards];
  for (const k of Object.keys(LAYER_WILDCARDS) as LayerKey[]) if (s.config.layers[k]) pile.push(...(LAYER_WILDCARDS[k] ?? []));
  let drawPile = rng.shuffle(pile);
  const turns = s.config.pacing.scriptTurns;
  const out: ScriptTurn[] = [];
  turns.forEach((st, i) => {
    const row = preset.script[Math.max(0, Math.min(9, st - 1))]!;
    const events: string[] = [];
    for (const e of row.events) {
      if (e === 'W') {
        if (drawPile.length === 0) drawPile = rng.shuffle(pile);
        const w = drawPile.shift();
        if (w) events.push(w);
      } else events.push(e);
    }
    out.push({ gameTurn: i + 1, scriptTurn: st, arrivals: row.arrivals, events, handover: i === turns.length - 1 });
  });
  // E35 drawn before the last turn is held and announced on the last turn
  const last = out[out.length - 1]!;
  for (const tt of out.slice(0, -1)) {
    if (tt.events.includes('E35')) {
      tt.events = tt.events.filter((e) => e !== 'E35');
      if (!last.events.includes('E35')) last.events.push('E35');
    }
  }
  return out;
}

// ───────────────────────────── start shift ─────────────────────────────

export function dealObjectives(s: SessionState, t: TableState, rng: Rng): void {
  const preset = PRESETS[s.config.preset];
  const pool = rng.shuffle(OBJECTIVES.filter(o => (o.group !== 'regional' || preset.regionalObjectives)
    && (o.key !== 'AHEAD_OF_CURVE' || (s.clock.phase === 'ACT' && t.tableStats.firstOverflowTurn === null))));
  const used = new Set<string>();
  for (const r of seatedRoles(t)) {
    const card = pool.find((o) => !used.has(o.key) && (!o.seats || o.seats.includes(r)));
    if (card) {
      used.add(card.key);
      t.seats[r]!.objective = card.key;
    }
  }
  t.dashboard.objectivesDealt = seatedRoles(t).filter((r) => t.seats[r]!.objective).length;
}

/** Prepare seating, resources and decks before the opening release. */
export function startShift(ctx: Ctx): void {
  const s = ctx.s;
  ensure(s.clock.phase === 'SETUP', 'The shift has already started.');
  const preset = PRESETS[s.config.preset];
  for (const t of s.tables) {
    for (const r of ['EM', 'SURG', 'PHYS', 'ICU'] as Role[]) {
      ensure(t.seats[r], `Table ${t.no} needs ${r === 'EM' ? 'Emergency' : r === 'SURG' ? 'a Surgeon' : r === 'PHYS' ? 'a Physician' : 'an Intensivist'} before the shift can start.`);
    }
    t.fiveSeat = !!t.seats.PSYCH;
  }
  s.script = buildScript(s, ctx.rng);
  s.clock.lastTurn = s.script.length;

  for (const t of s.tables) {
    const deck = buildTableDeck(s, t.no, t.fiveSeat, ctx.rng);
    t.deck = deck.order;
    // layers
    if (s.config.layers.staff) {
      for (const r of seatedRoles(t)) t.seats[r]!.staff = preset.staff[r];
      if (!t.fiveSeat) t.seats.EM!.staff += preset.staff.PSYCH;
    }
    if (s.config.layers.vents) t.board.vents = { total: preset.vents, recalledUntil: 0, recalled: 0 };
    if (s.config.layers.blood) t.board.blood = { units: preset.blood, orders: [], used: 0, wasted: 0 };
    // pre-loads: Physician 2, Surgeon 1 (confirmed on admission), Emergency 2 (arrivals)
    const preload = (role: Role, ids: string[], confirmed: boolean) => {
      for (const id of ids) {
        const p = s.patients[id]!;
        p.status = 'ACTIVE';
        p.arrivedTurn = 0;
        s.backs[id]!.drawn = true;
        if (confirmed) {
          p.box = 100;
          p.needsStabilise = false;
        }
        place(s, t, role, p, 'auto');
      }
    };
    preload('PHYS', deck.preload.PHYS, true);
    preload('SURG', deck.preload.SURG, true);
    preload('EM', deck.preload.EM, false);
    publishBedCounts(s, t);
  }
  s.clock.startedAt = ctx.at;
  emit(ctx, { type: 'SHIFT_STARTED', text: `Shift started: ${preset.label}, ${s.tables.length} table${s.tables.length > 1 ? 's' : ''}.`, severity: 1, audience: ['faculty'] });
}
